"use client"

import { useCallback, useEffect, useState } from "react";
import Link from "next/link"
import { Poppins } from "next/font/google";
import Image from "next/image";
import { usePathname } from "next/navigation";

import { useCurrentBranch } from "@/context/current-branch-provider";
import { BranchEvent, BranchPromotion } from "@/types/popadvise";
import Fetch from "@/lib/fetch";
import { DefaultBranchSelector } from "./branch-selector";
import { Menu, MessageSquare } from "lucide-react";
import NavigationMenu from "./mobile-menu";
import { Button } from "@/components/ui/button";
import { useChatbot } from "@/context/chatbot-provider";

const poppins = Poppins({
    subsets: ['latin'],
    weight: ['400', '500', '600', '700'],
})

export function DefaultHeader() {

    const [openMenu, setOpenMenu] = useState(false)
    const { brand, currentBranch } = useCurrentBranch()
    const [promotions, setPromotions] = useState<BranchPromotion[]>([])
    const [events, setEvents] = useState<BranchEvent[]>([])
    const { setOpen: setOpenChat } = useChatbot()
    const pathname = usePathname()
    const searchParams = new URLSearchParams()
    searchParams.append("branch", currentBranch.id)

    const getPromotions = useCallback(() => {
        Fetch("/v1/branch-promotions/active?bid=" + currentBranch.id).then((res) => {
            if (!res.ok) {
                throw new Error("No se pudo cargar las promociones")
            }
            return res.json()
        }).then((data) => {
            setPromotions(data)
        }).catch((err) => {
            console.error(err)
        })
    }, [currentBranch.id])

    const getEvents = useCallback(() => {
        Fetch("/v1/branch-events?bid=" + currentBranch.id).then((res) => {
            if (!res.ok) {
                throw new Error("No se pudo cargar las promociones")
            }
            return res.json()
        }).then((data) => {
            setEvents(data)
        }).catch((err) => {
            console.error(err)
        })
    }, [currentBranch.id])

    useEffect(() => {
        getPromotions()
        getEvents()
    }, [getPromotions, getEvents])


    return (
        <header className={`fixed h-32 top-0 inset-x-0 z-30 flex items-center justify-between bg-linear-to-b from-white md:from-[#1B1A15] to-transparent backdrop-blur-xs bg-opacity-50 ${poppins.className}`}>
            <section className="w-full max-w-[1800px] px-8 mx-auto flex justify-between md:grid md:grid-cols-3 items-center">
                {/* Mobile items */}
                <div className="flex items-center gap-3 md:hidden">
                    <Button variant="ghost" size="icon" onClick={() => setOpenMenu(true)}>
                        <Menu className="min-w-[50px] min-h-[32px]" />
                    </Button>
                    <Image
                        src={brand.logo.url}
                        alt="logo" width={36}
                        height={36}
                        className="object-cover rounded-full drop-shadow-lg w-[42px] h-[42px]"
                    />
                </div>
                <button onClick={() => setOpenChat((state) => !state)} className="p-2 bg-white rounded-lg flex items-center justify-center border shadow-xs md:hidden">
                    <MessageSquare className="h-6 w-6" />
                </button>

                {/* Desktop items */}
                <nav className="hidden md:block text-white font-normal">
                    <ul className="flex items-center gap-5 xl:gap-8 font-semibold text-sm xl:text-base">
                        <li>
                            {
                                pathname === "/reservations" ? (
                                    <Link href="/">Inicio</Link>
                                ) : (
                                    <Link href={`/reservations?${searchParams.toString()}`}>Reservaciones</Link>
                                )
                            }
                        </li>
                        {
                            events.length > 0 && pathname !== "/reservations" && (
                                <li>
                                    <Link href="#events">Eventos</Link>
                                </li>
                            )
                        }
                        {
                            promotions.length > 0 && pathname !== "/reservations" && (
                                <li>
                                    <Link href="#promotions">Promociones</Link>
                                </li>
                            )
                        }
                    </ul>
                </nav>
                <figure className="hidden md:block mx-auto">
                    <Image src={brand.logo.url} alt="logo" width={80} height={80} className="object-cover rounded-full drop-shadow-lg w-16 h-16 xl:w-20 xl:h-20" />
                </figure>
                <div className="hidden md:block">
                    <DefaultBranchSelector />
                </div>
            </section>
            <NavigationMenu
                promotions={promotions}
                events={events}
                open={openMenu}
                setOpen={setOpenMenu}
            />
        </header>
    )
}